import { useState, useEffect, useRef } from 'react';

interface Props {
  sessionId: string;
  apiBase: string;
  onSuccess: () => void;
  onError: (msg: string) => void;
}

/**
 * FirebaseOneTab — Google Sign-In para identificação do usuário no celular.
 * Fase A do fluxo de bonding: obtém Firebase ID Token e envia ao backend.
 *
 * Doc: docs_autentica_seguranca_hard/01-fluxo-mobile-biometria.md — Fase A
 *
 * Fluxo:
 *  1. Tenta signInWithPopup (funciona em desktop + alguns mobile)
 *  2. Se popup bloqueado → signInWithRedirect (mobile padrão)
 *  3. Ao retornar do redirect → useEffect captura getRedirectResult
 */
export function FirebaseOneTab({ sessionId, apiBase, onSuccess, onError }: Props) {
  const [loading, setLoading] = useState(false);
  const [checkingRedirect, setCheckingRedirect] = useState(true);
  const processedRef = useRef(false);

  const enviarTokenAoBackend = async (firebaseToken: string) => {
    const res = await fetch(`${apiBase}/auth/session/${sessionId}/identify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ firebaseToken }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Identificação negada');
    }
    const data = await res.json();

    // Persistir credenciais JWT para o PWA manter sessão entre reinícios
    if (data.access_token) {
      localStorage.setItem('ac_token', data.access_token);
      // JWT salvo silenciosamente
    }
    if (data.refresh_token) {
      localStorage.setItem('ac_refresh_token', data.refresh_token);
    }
    if (data.user) {
      localStorage.setItem('ac_user', JSON.stringify(data.user));
      if (data.user.perfil) localStorage.setItem('ac_perfil', data.user.perfil);
      if (data.user.escolaId) localStorage.setItem('ac_escolaId', data.user.escolaId);
      if (data.user.nomeEscola) localStorage.setItem('ac_nomeEscola', data.user.nomeEscola);
      if (data.user.cargo) localStorage.setItem('ac_cargo', data.user.cargo);
    }

    onSuccess();
  };

  /** Inicializa Firebase e retorna { auth, GoogleAuthProvider } */
  const initFirebase = async () => {
    const { initializeApp, getApps } = await import('firebase/app');
    const firebaseAuth = await import('firebase/auth');

    const firebaseConfig = {
      apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId:             import.meta.env.VITE_FIREBASE_APP_ID,
    };

    const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
    const auth = firebaseAuth.getAuth(app);
    return { auth, firebaseAuth };
  };

  // ── Ao montar: verificar se é retorno de redirect ──────────────────────
  useEffect(() => {
    if (processedRef.current) return;

    (async () => {
      try {
        console.log('[FirebaseOneTab] Checking redirect result...');
        const { auth, firebaseAuth } = await initFirebase();
        const redirectResult = await firebaseAuth.getRedirectResult(auth);

        if (redirectResult?.user) {
          processedRef.current = true;
          setLoading(true);
          const firebaseToken = await redirectResult.user.getIdToken();
          await enviarTokenAoBackend(firebaseToken);
          return;
        }

      } catch (e: any) {
        console.error('[FirebaseOneTab] Redirect check error:', e.code, e.message);
        onError(`Erro Firebase (${e.code || 'unknown'}): ${e.message}`);
      } finally {
        setCheckingRedirect(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tentarGoogleLogin = async () => {
    setLoading(true);
    try {
      const { auth, firebaseAuth } = await initFirebase();
      const provider = new firebaseAuth.GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });

      const isLocal =
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.endsWith('ngrok-free.dev') ||
        window.location.hostname.startsWith('192.168.') ||
        window.location.hostname.startsWith('10.') ||
        window.location.hostname.startsWith('172.');


      if (isLocal) {
        // Em dev local: popup funciona bem
        try {
          const result = await firebaseAuth.signInWithPopup(auth, provider);
          const firebaseToken = await result.user.getIdToken();
          await enviarTokenAoBackend(firebaseToken);
        } catch (popupErr: any) {
          if (popupErr.code === 'auth/popup-blocked' ||
              popupErr.code === 'auth/popup-closed-by-user' ||
              popupErr.code === 'auth/cancelled-popup-request') {
            sessionStorage.setItem('bonding_session_id', sessionId);
            localStorage.setItem('bonding_session_id', sessionId);
            await firebaseAuth.signInWithRedirect(auth, provider);
            return;
          }
          throw popupErr;
        }
      } else {
        // Em produção: redirect direto (popup falha com COOP headers)
        sessionStorage.setItem('bonding_session_id', sessionId);
        localStorage.setItem('bonding_session_id', sessionId);
        await firebaseAuth.signInWithRedirect(auth, provider);
        // A página vai redirecionar — o resultado é capturado no próximo mount
        return;
      }
    } catch (e: any) {
      const msg = e.code
        ? `Firebase: ${e.code} — ${e.message}`
        : (e.message || 'Erro ao autenticar com Google');
      onError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Enquanto verifica redirect, mostra spinner ─────────────────────────
  if (checkingRedirect) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
        <div style={{
          width: 40, height: 40, border: '3px solid rgba(255,255,255,.1)',
          borderTopColor: '#3b82f6', borderRadius: '50%',
          margin: '0 auto 16px', animation: 'spin 0.8s linear infinite',
        }} />
        <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Verificando autenticação...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="glass-card animate-fade-in-up" style={{ 
      textAlign: 'center', padding: '2.5rem 1.5rem', borderRadius: 24,
      position: 'relative', overflow: 'hidden'
    }}>
      {/* Glow background behind the card */}
      <div style={{
        position: 'absolute', top: -50, left: '50%', transform: 'translateX(-50%)',
        width: 150, height: 150, background: 'rgba(234, 179, 8, 0.1)',
        filter: 'blur(40px)', zIndex: 0
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{
          width: 80, height: 80, borderRadius: '50%', margin: '0 auto 1.5rem',
          background: 'linear-gradient(135deg, rgba(234,179,8,0.15), rgba(202,138,4,0.05))',
          border: '1px solid rgba(234,179,8,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 0 20px rgba(234,179,8,0.1)'
        }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </div>
        
        <h2 style={{ 
          fontSize: '1.4rem', fontWeight: 800, marginBottom: '0.5rem', 
          color: '#f8fafc', letterSpacing: '-0.02em' 
        }}>
          Confirme quem é você
        </h2>
        <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '2.5rem', lineHeight: 1.6 }}>
          Use sua conta Google cadastrada no sistema para comprovar sua identidade e iniciar a vinculação segura.
        </p>

        <button
          onClick={tentarGoogleLogin}
          disabled={loading}
          style={{
            width: '100%', padding: '1.1rem 1rem', borderRadius: 16,
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(255,255,255,0.03)',
            backdropFilter: 'blur(10px)',
            color: '#f8fafc', fontSize: '1rem', fontWeight: 600,
            cursor: loading ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
            transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
          onMouseOver={(e) => {
            if (!loading) e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
          }}
          onMouseOut={(e) => {
            if (!loading) e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
          }}
          onMouseDown={(e) => {
            if (!loading) e.currentTarget.style.transform = 'scale(0.98)';
          }}
          onMouseUp={(e) => {
            if (!loading) e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          {loading ? '⏳ Conectando...' : (
            <>
              <svg width="22" height="22" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Entrar com Google
            </>
          )}
        </button>

        <div style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
          <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0 }}>
            Conexão criptografada
          </p>
        </div>
      </div>
    </div>
  );
}
